import type {
  DocumentRevision,
  RevisionComparison,
  SafetyIssuePin,
} from "../types/domain";

const SAFETY_TERMS = [
  "access",
  "anchor",
  "barrier",
  "combustible",
  "confined space",
  "demolition",
  "egress",
  "emergency",
  "excavation",
  "fall",
  "fire",
  "hazard",
  "isolation",
  "lifting",
  "load",
  "maintenance",
  "permit",
  "rescue",
  "risk",
  "scaffold",
  "structural",
  "temporary works",
  "ventilation",
];

const compact = (value: string) => value.replace(/\s+/g, " ").trim();

const segments = (pages: string[]) =>
  pages
    .flatMap((page) => page.split(/(?<=[.;:])\s+|\n+/))
    .map(compact)
    .filter((value) => value.length >= 12);

export async function inspectRevisionFile(file: File): Promise<
  Pick<
    DocumentRevision,
    | "extractedText"
    | "extractedPages"
    | "textExtractionStatus"
    | "ifcSchema"
    | "ifcEntityCount"
    | "ifcSafetyEntities"
  >
> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".pdf")) {
    try {
      const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
      const worker = await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url");
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      const bytes = new Uint8Array(await file.arrayBuffer());
      const pdf = await pdfjs.getDocument({ data: bytes }).promise;
      const pages: string[] = [];
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
        const page = await pdf.getPage(pageNumber);
        const content = await page.getTextContent();
        pages.push(
          compact(
            content.items
              .map((item) => ("str" in item ? item.str : ""))
              .filter(Boolean)
              .join(" "),
          ),
        );
      }
      return {
        extractedText: pages.join("\n\n"),
        extractedPages: pages,
        textExtractionStatus: "complete",
        ifcSchema: "",
        ifcEntityCount: null,
        ifcSafetyEntities: [],
      };
    } catch {
      return {
        extractedText: "",
        extractedPages: [],
        textExtractionStatus: "failed",
        ifcSchema: "",
        ifcEntityCount: null,
        ifcSafetyEntities: [],
      };
    }
  }

  if (name.endsWith(".ifc")) {
    const text = await file.text();
    const schema = text.match(/FILE_SCHEMA\s*\(\s*\(\s*'([^']+)'/i)?.[1] ?? "Unknown";
    const entities = text.match(/^#\d+\s*=\s*IFC[A-Z0-9_]+/gim) ?? [];
    const safetyEntities = text
      .split(/\r?\n/)
      .filter((line) =>
        /IFC(PROTECTIVEDEVICE|RAILING|STAIR|RAMP|DOOR|SPACE|ZONE|SYSTEM|TASK|PERFORMANCEHISTORY)|Pset_.*(Fire|Safety|Risk)|FIRE|SAFETY|HAZARD/i.test(
          line,
        ),
      )
      .slice(0, 100)
      .map(compact);
    return {
      extractedText: safetyEntities.join("\n"),
      extractedPages: safetyEntities.length ? [safetyEntities.join("\n")] : [],
      textExtractionStatus: "complete",
      ifcSchema: schema,
      ifcEntityCount: entities.length,
      ifcSafetyEntities: safetyEntities,
    };
  }

  if (/\.(txt|csv)$/i.test(name)) {
    const text = await file.text();
    return {
      extractedText: text,
      extractedPages: [text],
      textExtractionStatus: "complete",
      ifcSchema: "",
      ifcEntityCount: null,
      ifcSafetyEntities: [],
    };
  }

  return {
    extractedText: "",
    extractedPages: [],
    textExtractionStatus: "unsupported",
    ifcSchema: "",
    ifcEntityCount: null,
    ifcSafetyEntities: [],
  };
}

export function compareRevisions(
  previous: DocumentRevision,
  current: DocumentRevision,
): RevisionComparison {
  const previousSegments = segments(previous.extractedPages);
  const currentSegments = segments(current.extractedPages);
  const previousSet = new Set(previousSegments.map((value) => value.toLowerCase()));
  const currentSet = new Set(currentSegments.map((value) => value.toLowerCase()));
  const addedText = currentSegments
    .filter((value) => !previousSet.has(value.toLowerCase()))
    .slice(0, 120);
  const removedText = previousSegments
    .filter((value) => !currentSet.has(value.toLowerCase()))
    .slice(0, 120);
  const pageCount = Math.max(previous.extractedPages.length, current.extractedPages.length);
  const changedPages = Array.from({ length: pageCount }, (_, index) => index)
    .filter(
      (index) =>
        compact(previous.extractedPages[index] ?? "").toLowerCase() !==
        compact(current.extractedPages[index] ?? "").toLowerCase(),
    )
    .map((index) => index + 1);
  const changedText = [...addedText, ...removedText].join(" ").toLowerCase();
  const safetyKeywords = SAFETY_TERMS.filter((term) => changedText.includes(term));
  const denominator = Math.max(previousSegments.length, currentSegments.length, 1);
  const changeScore = Math.min(
    100,
    Math.round(((addedText.length + removedText.length) / denominator) * 100),
  );
  const impactLevel =
    safetyKeywords.length >= 3 || changeScore >= 35
      ? "high"
      : safetyKeywords.length > 0 || changeScore >= 15
        ? "medium"
        : changeScore > 0
          ? "low"
          : "none";
  const impactedRiskIds = Array.from(
    new Set([...previous.linkedRiskIds, ...current.linkedRiskIds]),
  );

  return {
    id: crypto.randomUUID(),
    previousRevisionId: previous.id,
    currentRevisionId: current.id,
    comparedAt: new Date().toISOString(),
    engineVersion: "DSM-DIFF-1.0",
    addedText,
    removedText,
    changedPages,
    safetyKeywords,
    changeScore,
    impactLevel,
    impactedRiskIds,
    reopenedRiskIds: impactLevel === "medium" || impactLevel === "high" ? impactedRiskIds : [],
    reviewStatus: "machine-screened",
    reviewer: "",
    reviewNotes: "",
  };
}

export async function exportBcfIssues(
  document: DocumentRevision,
  pins: SafetyIssuePin[],
): Promise<Blob> {
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  zip.file(
    "bcf.version",
    `<?xml version="1.0" encoding="UTF-8"?><Version VersionId="2.1"/>`,
  );
  for (const pin of pins) {
    const guid = pin.bcfGuid || crypto.randomUUID();
    const folder = zip.folder(guid);
    folder?.file(
      "markup.bcf",
      `<?xml version="1.0" encoding="UTF-8"?>
<Markup>
  <Header><File IfcProject="" IfcSpatialStructureElement=""><Filename>${escapeXml(document.fileName || document.documentNumber)}</Filename></File></Header>
  <Topic Guid="${guid}" TopicType="Design Safety" TopicStatus="${escapeXml(pin.status)}">
    <ReferenceLink>${escapeXml(`${document.documentNumber} ${document.revision} page ${pin.page}`)}</ReferenceLink>
    <Title>${escapeXml(pin.title)}</Title>
    <Priority>${escapeXml(pin.priority)}</Priority>
    <Description>${escapeXml(pin.description)}</Description>
    <AssignedTo>${escapeXml(pin.owner)}</AssignedTo>
    <DueDate>${escapeXml(pin.dueDate)}</DueDate>
  </Topic>
</Markup>`,
    );
  }
  return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}

export async function importBcfIssues(
  file: File,
  documentRevisionId: string,
): Promise<SafetyIssuePin[]> {
  const { default: JSZip } = await import("jszip");
  const zip = await JSZip.loadAsync(file);
  const markupFiles = Object.values(zip.files).filter((entry) =>
    entry.name.toLowerCase().endsWith("markup.bcf"),
  );
  const imported: SafetyIssuePin[] = [];
  for (const entry of markupFiles) {
    const xml = await entry.async("text");
    const value = (tag: string) =>
      compact(xml.match(new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, "i"))?.[1] ?? "");
    imported.push({
      id: crypto.randomUUID(),
      documentRevisionId,
      page: Number(value("ReferenceLink").match(/page\s+(\d+)/i)?.[1] ?? 1),
      x: 50,
      y: 50,
      title: decodeXml(value("Title")) || "Imported BCF safety issue",
      description: decodeXml(value("Description")),
      linkedRiskId: "",
      owner: decodeXml(value("AssignedTo")),
      dueDate: value("DueDate"),
      status: "open",
      bcfGuid: xml.match(/<Topic[^>]*Guid="([^"]+)"/i)?.[1] ?? crypto.randomUUID(),
      priority: normalisePriority(value("Priority")),
      viewpointReference: "",
      createdAt: new Date().toISOString(),
    });
  }
  return imported;
}

const normalisePriority = (value: string): SafetyIssuePin["priority"] => {
  const priority = value.toLowerCase();
  if (priority === "low" || priority === "high" || priority === "critical") return priority;
  return "normal";
};

const escapeXml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");

const decodeXml = (value: string) =>
  value
    .replaceAll("&apos;", "'")
    .replaceAll("&quot;", '"')
    .replaceAll("&gt;", ">")
    .replaceAll("&lt;", "<")
    .replaceAll("&amp;", "&");
